import { Component, Input, OnInit, booleanAttribute, numberAttribute } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ForeignKeyValidator } from 'src/app/components/core/_infrastructure/classes/foreignKeyValidator';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { NoWhiteSpaceValidator } from 'src/app/components/core/_infrastructure/classes/noWhiteSpaceValidator';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { RecintoService } from '../recinto.service';
import { TwRecintoViewModel } from '../recinto.types';
import { GlobalService } from 'src/app/components/core/_infrastructure/classes/services/global.service';
import { Tc } from 'src/app/components/core/_infrastructure/types/common';
import { RecintoInfoRequestViewModel } from 'src/app/components/_exportacion/atestaciones/lugarproduccion/lugarproduccion.types';
import { NavMenuService } from 'src/app/components/core/components/navmenu/navmenu.service';
import { CustomValidator } from 'src/app/components/core/_infrastructure/classes/customValidator';
import { EnumDescripcionTcTipoSolicitudCamSingular, EnumIdsTcTipoSolicitudCam } from '../../types/enum';

@Component({
    selector: 'app-recinto-wrapper-edit',
    templateUrl: './recinto-wrapper-edit.component.html',
    styleUrls: ['./recinto-wrapper-edit.component.scss'],
})
export class RecintoWrapperEditComponent extends ComponentBase implements OnInit {
    @Input({ transform: numberAttribute }) id = 0; // Id del recinto
    @Input({ transform: booleanAttribute }) editMode = true;
    @Input({ transform: booleanAttribute }) dialogMode = false;

    /**
     * @article: How to Use @Input() to Read Angular Route Parameters
     * https://www.freecodecamp.org/news/use-input-for-angular-route-parameters/
     */

    /**
     * @article: Reactive forms with smart dumb components
     * https://medium.com/angular-athens/reactive-forms-with-smart-dumb-components-48ebc81417c7
     */
    visibilitySpinner = false;
    visibilitySpinnerSave = false;
    visibilitySpinnerValidate = false;
    recinto: TwRecintoViewModel;
    formGroup: FormGroup;

    provincias: Tc[];
    municipios: Tc[];
    tiposRecinto: Tc[];

    private messageErrorAlreadyExist = 'Ya existe un Recinto para el mismo código SigPac';
    private successMessage = 'Recinto modificado correctamente';
    private listUrl = '/campanias/recintos';
    private editionUrl = '/campanias/recinto/edit/';
    private invalidSigPacMessage = 'No se han obtenido resultados para éste recinto.';
    private sigpacValidated = 'El Código SigPac es válido';
    private superficieTotalHasChangedError = 'La superficie total ha cambiado. Por favor, revise y reestructure la superficie utilizada por sus cultivos.';

    private titleVisorSigPac = 'Visor SIGPAC';
    private URL_VISOR_SIGPAC = 'http://sigpac.mapama.gob.es/fega/visor/?';
    sigpacInvalid = true;

    constructor(
        private fb: FormBuilder,
        private router: Router,
        private recintoService: RecintoService,
        private globalService: GlobalService,
        private navMenuService: NavMenuService,
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.preparePresentation();
        this.processInput();
    }

    private processInput(): void {
        if (this.id) {
            this.getRecinto(this.id);
        }
    }

    private preparePresentation(): void {
        this.getListas();
        this.buildForm();
    }

    private buildForm(): void {
        this.formGroup = this.fb.group({
            Id: [null],
            Titular: [null, Validators.compose([Validators.required, NoWhiteSpaceValidator.cannotContainSpace])],
            Nif: [null, Validators.compose([Validators.required, CustomValidator.NIFValidator])],
            IdTipoRecinto: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            IdProvincia: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            IdMunicipio: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            IdMunicipioSigPac: [null, Validators.compose([Validators.required])],
            Agregado: [null, Validators.compose([Validators.required])],
            Zona: [null, Validators.compose([Validators.required])],
            Poligono: [null, Validators.compose([Validators.required])],
            Parcela: [null, Validators.compose([Validators.required])],
            Recinto: [null, Validators.compose([Validators.required])],
            SuperficieTotal: [null, Validators.compose([Validators.required, ForeignKeyValidator.cannotBeNullOrZero])],
            UsoSigPac: [null, Validators.compose([Validators.required])], // hidden
            EsCompleta: [null], // hidden
            FechaCreacion: [null], // hidden
        });
        this.form = this.formGroup;
    }

    private getRecinto(id: number): void {
        this.visibilitySpinner = true;
        this.recintoService
            .getRecinto(id)
            .pipe(
                tap((vm: TwRecintoViewModel) => {
                    this.visibilitySpinner = false;
                    this.recinto = vm;
                    this.navMenuService.updateMenuServiceForRecinto(vm.Id!);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.visibilitySpinner = false;
                    this.formGroup?.disable();
                    return of([]);
                }),
            )
            .subscribe();
    }

    private getListas(): void {
        this.globalService.getProvincias().subscribe((provincias: Tc[]) => {
            this.provincias = provincias;
        });
        // Invocar a servicio para obtener los tipos de recinto, que son los mismos que se usan en los tipos de solicitud de Campaña
        // excepto los tipos Confección/Almacén o Emp. Deshidratadora, que habrá que sacarlos de ésta funcionalidad, para que no aparezca en el desplegable.
        this.globalService.getTipoSolicitudCam().subscribe((tiposSolicitudCam: Tc[]) => {
            this.tiposRecinto = tiposSolicitudCam?.filter(
                tipoSolicitudCam =>
                    tipoSolicitudCam.Id !== EnumIdsTcTipoSolicitudCam.ID_CONFECCION &&
                    tipoSolicitudCam.Id !== EnumIdsTcTipoSolicitudCam.ID_EMPRESA_DESHIDRATACION,
            );
        });
    }

    /**
     * Invoked when the user selects a 'provincia' in the filter
     * @param provincia number
     * @return void
     */
    onProvinciaSelected(provincia: number): void {
        this.globalService.getMunicipiosComplete(provincia).subscribe((municipios: any[]) => {
            this.municipios = municipios;
        });
    }

    onSave(): void {
        this.visibilitySpinnerSave = true;
        const formRawValue = this.formGroup.getRawValue();

        const model: TwRecintoViewModel = { ...formRawValue };
        if (!model.IdUsuario) {
            model.IdUsuario = this.authService.getLoggedInUser()?.Id || 0;
        }
        if (this.recinto?.FechaCreacion) {
            model.FechaCreacion = this.recinto?.FechaCreacion;
        }

        model.EsCompleta = model.EsCompleta ? 1 : 0;

        this.recintoService
            .saveRecinto(model)
            .pipe(
                tap(res => {
                    this.visibilitySpinnerSave = false;
                    this.addSuccessMessage(this.successMessage);

                    if (!model.Id) {
                        let IdRecinto = res;
                        this.gotoEdition(IdRecinto);
                    }
                }),
                catchError(error => {
                    this.visibilitySpinnerSave = false;
                    this.catchServerError(error);
                    if (error?.message?.indexOf(this.messageErrorAlreadyExist) != -1) {
                        this.formGroup?.disable();
                    }
                    return of([]);
                }),
            )
            .subscribe();
    }

    onBack(): void {
        this.router.navigate([this.listUrl]);
    }

    private gotoEdition(IdRecinto: number): void {
        this.router.navigate([this.editionUrl, IdRecinto]);
    }

    validateOnSigPac(): void {
        const recintoRequestVM = this.codigoSigPac;
        this.visibilitySpinnerValidate = true;
        this.recintoService
            .validarParcelaSigpac(recintoRequestVM)
            .pipe(
                tap(response => {
                    this.onSigpacValid(response);
                }),
                catchError(response => {
                    this.visibilitySpinnerValidate = false;
                    if (response?.error?.error?.indexOf('An error occurred while sending the request')) {
                        this.addErrorMessage('No se ha podido establecer conexión con el servicio de validación de SigPac. Vuelva a intentarlo más tarde');
                    } else {
                        this.catchServerError(response);
                    }
                    return of(null);
                }),
            )
            .subscribe();
    }

    private onSigpacValid(response: any): void {
        this.visibilitySpinnerValidate = false;
        if (!response || !response?.Superficie) {
            this.addErrorMessage(this.invalidSigPacMessage);
            return;
        }
        const currentSuperficieTotal = this.roundSuperficie(response?.Superficie); // Para mostrarlo en hectáreas

        this.sigpacInvalid = false;
        this.formGroup.patchValue({
            SuperficieTotal: currentSuperficieTotal,
            UsoSigPac: response.Uso_sigpac,
        });
        this.formGroup.markAsDirty();

        // const superficieTotalHasChanged = this.superficieTotalInvalid(this.recinto.SuperficieTotal, currentSuperficieTotal);
        if (this.recinto && this.recinto?.SuperficieTotal !== currentSuperficieTotal) {
            this.addErrorMessage(this.superficieTotalHasChangedError + ': ' + this.recinto.SuperficieTotal + ' <> ' + currentSuperficieTotal);
            // @author: scalero1 - No se guarda el recinto como inválido (habría que crear la columna), porque al incluir el recinto en la solicitud, se vuelve a validar.
        } else {
            this.addSuccessMessage(this.sigpacValidated);
        }
    }

    /**
     * Si la superficie total ha cambiado respecto de la anterior,
     * puede ocurrir que la superficie que utilizan todos los cultivos del recinto
     * sea superior, lo cual sería un error.
     * @param previous number
     * @param current number
     * @returns boolean
     */
    // @scalero1: Dejo éste código por si vemos necesario impedir al usuario guardar el recinto bajo ésta condición
    private superficieTotalInvalid(previous: number, current: number | null): boolean {
        if (previous !== current && current !== null) {
            this.addErrorMessage(this.superficieTotalHasChangedError + ': ' + previous + ' <> ' + current);
            this.setCustomError('SuperficieTotalChanged', 'SuperficieTotal');
            this.formGroup.controls['SuperficieTotal'].markAsTouched();
            return true;
        } else {
            this.removeCustomError('SuperficieTotalChanged', 'SuperficieTotal');
            return false;
        }
    }

    private roundSuperficie(superficie: number): number | null {
        if (superficie == null || superficie == 0) return null;
        const s = superficie / 10000;
        const r = Math.round((s + Number.EPSILON) * 10000) / 10000;
        return r || null;
    }

    /**
     * Se abrirá la ventana de sigpac (url en sigpac) a partir del código
     */
    onShowSigpac(): void {
        // @author: scalero1 Se podría reemplazar por llamada al método getUrlVisorSIGPAC de configurationService
        const codigoSigpac: string = this.mapearUrlVisorSigpac();
        const urlVisorSigPac = this.URL_VISOR_SIGPAC + codigoSigpac;

        if (codigoSigpac != '') {
            if (confirm('Este enlace lo llevará a un sitio web externo, ¿desea continuar?')) {
                window.open(
                    urlVisorSigPac,
                    this.titleVisorSigPac,
                    'toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=780,height=600,top=' +
                        (screen.height - 400) +
                        ',left=' +
                        (screen.width - 840),
                );
            }
        }
    }

    private mapearUrlVisorSigpac(): string {
        const codigoSigPac = this.codigoSigPac;

        let url =
            'provincia=' +
            codigoSigPac.Provincia?.toString() +
            '&' +
            'municipio=' +
            codigoSigPac.Municipio?.toString() +
            '&' +
            'agregado=' +
            codigoSigPac.Agregado?.toString() +
            '&' +
            'zona=' +
            codigoSigPac.Zona?.toString() +
            '&' +
            'poligono=' +
            codigoSigPac.Poligono?.toString() +
            '&' +
            'parcela=' +
            codigoSigPac.Parcela?.toString() +
            '&' +
            'recinto=' +
            codigoSigPac.Recinto?.toString();

        return url;
    }

    get allowToSave(): boolean {
        return !this.formInvalid && this.editMode;
    }

    get formInvalid(): boolean {
        return !this.formGroup.valid || !this.formGroup.dirty || this.formGroup.pristine;
    }

    get codigoSigpacNoExiste(): boolean {
        const codigoSigPac = this.codigoSigPac;
        const codigo =
            codigoSigPac.Provincia !== null &&
            codigoSigPac.Provincia !== undefined &&
            codigoSigPac.Municipio !== null &&
            codigoSigPac.Municipio !== undefined &&
            codigoSigPac.Agregado !== null &&
            codigoSigPac.Agregado !== undefined &&
            codigoSigPac.Zona !== null &&
            codigoSigPac.Zona !== undefined &&
            codigoSigPac.Poligono !== null &&
            codigoSigPac.Poligono !== undefined &&
            codigoSigPac.Parcela !== null &&
            codigoSigPac.Parcela !== undefined &&
            codigoSigPac.Recinto !== null &&
            codigoSigPac.Recinto !== undefined;
        return !codigo;
    }

    get codigoSigPac(): RecintoInfoRequestViewModel {
        const recintoRequestVM = new RecintoInfoRequestViewModel();

        const formRawValue = this.formGroup.getRawValue();

        recintoRequestVM.Provincia = formRawValue.IdProvincia?.toString();
        recintoRequestVM.Municipio = formRawValue.IdMunicipioSigPac?.toString();
        recintoRequestVM.Agregado = formRawValue.Agregado?.toString();
        recintoRequestVM.Zona = formRawValue.Zona?.toString();
        recintoRequestVM.Poligono = formRawValue.Poligono?.toString();
        recintoRequestVM.Parcela = formRawValue.Parcela?.toString();
        recintoRequestVM.Recinto = formRawValue.Recinto?.toString();

        return recintoRequestVM;
    }

    get title(): string {
        if (!this.recinto) return '';

        switch (this.recinto?.IdTipoRecinto) {
            case EnumIdsTcTipoSolicitudCam.ID_PARCELA:
                return EnumDescripcionTcTipoSolicitudCamSingular.PARCELA;
            case EnumIdsTcTipoSolicitudCam.ID_INVERNADERO:
                return EnumDescripcionTcTipoSolicitudCamSingular.INVERNADERO;
            default:
                return EnumDescripcionTcTipoSolicitudCamSingular.RECINTO;
        }
    }
}
